import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../../styles/colors';
import { FlexboxCenter, FlexboxVertical } from '../Layout';
import { Headline6, TextDefault } from '../Text';
import { Icon } from '../Icon';

const Wrapper = styled(FlexboxCenter)`
  position: relative;
  display: inline-flex;
  margin: 0 0 0 .5rem;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid ${Colors.gray};
  border-radius: 50%;
  color: ${Colors.primary};
  font-size: .875rem;
  line-height: .875rem;
  font-weight: 700;
  transform: translateY(-1px);
  z-index: 4;
  cursor: pointer;
`;

const PopupWrapper = styled(FlexboxVertical)<{ visible?: boolean, below?: boolean }>`
  position: absolute;
  display: ${({ visible = false }) => visible ? 'flex' : 'none'};
  z-index: 500;

  ${({ below = false }) => below ? 'top' : 'bottom'}: calc(100% + 1.5rem);
  background: ${Colors.background};
  border: 2px solid ${Colors.gray};
  padding: 1rem;
  min-width: 12rem;
  
  div > & > ${Headline6} {
    margin-bottom: .5rem;
    color: ${Colors.primary};
  }
  div > & > ${TextDefault} {
    margin: 0;
    color: ${Colors.grayDark};
  }
  
  &:before {
    content: '';
    position: absolute;
    ${({ below = false }) => below ? 'bottom' : 'top'}: 100%;
    left: calc(50% - .75rem);
    border-left: .75rem solid transparent;
    border-right: .75rem solid transparent;
    border-${({ below = false }) => below ? 'bottom' : 'top'}: 1rem solid ${Colors.gray};
  }
  &:after {
    content: '';
    position: absolute;
    ${({ below = false }) => below ? 'bottom' : 'top'}: calc(100% - 3px); 
    left: calc(50% - .75rem);
    border-left: .75rem solid transparent;
    border-right: .75rem solid transparent;
    border-${({ below = false }) => below ? 'bottom' : 'top'}: 1rem solid ${Colors.background};
  }
`;

interface Props {
  //
}

export const Hint: FC<Props> = ({ ...props }) => {
  const [visible, setVisible] = useState(false);
  const [hoverWrapper, setHoverWrapper] = useState(false);
  const [hoverPopupWrapper, setHoverPopupWrapper] = useState(false);
  const [toggleBelow, setToggleBelow] = useState(false);
  const hoverWrapperHandle = useRef<any>(null);
  const hoverPopupWrapperHandle = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hoverPopupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setVisible(hoverWrapper || hoverPopupWrapper);
  }, [hoverWrapper, hoverPopupWrapper]);

  useEffect(() => {
    if (wrapperRef.current && hoverPopupRef.current) {
      if (wrapperRef.current.offsetTop < hoverPopupRef.current.offsetHeight) {
        setToggleBelow(true);
      }
    }
  }, [wrapperRef.current, hoverPopupRef.current, visible]);

  return (
    <Wrapper
      ref={wrapperRef}
      onMouseEnter={() => {
        if (hoverWrapperHandle.current) {
          clearTimeout(hoverWrapperHandle.current);
        }
        setHoverWrapper(true);
      }}
      onMouseLeave={() => {
        if (hoverWrapperHandle.current) {
          clearTimeout(hoverWrapperHandle.current);
        }
        hoverWrapperHandle.current = setTimeout(() => setHoverWrapper(false), 200);
      }}>
      <Icon icon={'question-mark'} size={10} />
      <PopupWrapper
        ref={hoverPopupRef}
        visible={visible}
        below={toggleBelow}
        onMouseEnter={() => {
          if (hoverPopupWrapperHandle.current) {
            clearTimeout(hoverPopupWrapperHandle.current);
          }
          setHoverPopupWrapper(true);
        }}
        onMouseLeave={() => {
          if (hoverPopupWrapperHandle.current) {
            clearTimeout(hoverPopupWrapperHandle.current);
          }
          hoverPopupWrapperHandle.current = setTimeout(() => setHoverPopupWrapper(false), 200);
        }}
      >
        <Headline6>Hinweis</Headline6>
        <TextDefault>
          {props.children}
        </TextDefault>
      </PopupWrapper>
    </Wrapper>
  );
};
